import { useState, useCallback } from 'react';
import { useDebounce } from './useDebounce';

export interface UrlMetadata {
  title: string;
  description: string;
  imageUrl: string | null;
}

export interface UseManualUrlFetchReturn {
  metadata: UrlMetadata | undefined;
  loading: boolean;
  error: string | undefined;
  fetchMetadata: (url: string) => Promise<void>;
  isValidUrl: (url: string) => boolean;
}

const isValidUrl = (url: string): boolean => {
  try {
    new URL(url);
    return url.startsWith('http://') || url.startsWith('https://');
  } catch {
    return false;
  }
};

export const useUrlMetadata = (): UseManualUrlFetchReturn => {
  const [metadata, setMetadata] = useState<UrlMetadata | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);

  const fetchMetadata = useCallback(async (url: string) => {
    const trimmedUrl = url.trim();
    
    if (!isValidUrl(trimmedUrl)) {
      setError('Please enter a valid URL');
      setMetadata(undefined);
      return;
    }

    setLoading(true);
    setError(undefined);

    try {
      const response = await fetch(`/api/scraper?url=${encodeURIComponent(trimmedUrl)}`);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      const data: UrlMetadata = await response.json();
      setMetadata(data);
    } catch (err: any) {
      console.error('URL metadata fetch error:', err);
      setError(err.message || 'Failed to fetch URL metadata');
      
      // Try to fetch basic HTML title as fallback
      let fallbackTitle = '';
      try {
        const htmlResponse = await fetch(trimmedUrl);
        if (htmlResponse.ok) {
          const html = await htmlResponse.text();
          const titleMatch = html.match(/<title[^>]*>([^<]+)<\/title>/i);
          fallbackTitle = titleMatch ? titleMatch[1].trim() : '';
        }
      } catch {
        // Ignore fallback fetch errors
      }
      
      setMetadata({
        title: fallbackTitle,
        description: err.message || 'Failed to fetch URL metadata',
        imageUrl: null
      });
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    metadata,
    loading,
    error,
    fetchMetadata,
    isValidUrl,
  };
};